import * as Cesium from 'cesium';

const GLOW_GROUP_ID_PREFIX = 'GLOW_LAYER'; // parent group id base

export const removeExistingGlowEntities = (viewer: Cesium.Viewer, layerName?: string) => {
  if (!viewer) return;

  const prefix = `${GLOW_GROUP_ID_PREFIX}::${layerName ?? 'ALL'}::`;

  const toRemove: Cesium.Entity[] = [];

  viewer.entities.values.forEach((entity) => {
    try {
      if (typeof entity.id === 'string' && entity.id.startsWith(prefix)) {
        toRemove.push(entity);
        return;
      }

      if (entity.properties) {
        const now = Cesium.JulianDate.now();

        const isGlow = entity.properties.isGlow?.getValue?.(now);
        const isGrey = entity.properties.isGrey?.getValue?.(now);

        if (isGlow || isGrey) {
          toRemove.push(entity);
          return;
        }
      }
    } catch (e) {}
  });

  toRemove.forEach((e) => {
    try {
      viewer.entities.remove(e);
    } catch (err) {}
  });
};

export const getOuterRingCoords = (geom: any): number[][] => {
  if (!geom || !geom.type || !geom.coordinates) return [];

  let rings: any[] = [];

  if (geom.type === 'Polygon') {
    rings = geom.coordinates;
  } else if (geom.type === 'MultiPolygon') {
    rings = geom.coordinates.flat();
  }

  if (!Array.isArray(rings) || rings.length === 0) return [];

  const outer = rings[0];
  if (!Array.isArray(outer) || outer.length === 0) return [];

  const coords = outer.filter(
    (pair: any) => Array.isArray(pair) && typeof pair[0] === 'number' && typeof pair[1] === 'number' && !isNaN(pair[0]) && !isNaN(pair[1]),
  );

  if (coords.length > 0) {
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coords.push([first[0], first[1]]);
    }
  }

  return coords;
};

export const renderGreyscalePolygon = (viewer: Cesium.Viewer, feature: any, grayColor: Cesium.Color, layerName: string, idx: number) => {
  if (!viewer || !feature || !feature.geometry) return;

  const coords = getOuterRingCoords(feature.geometry);
  if (!coords || coords.length < 4) return;

  const positions = coords.map((p: number[]) => Cesium.Cartesian3.fromDegrees(p[0], p[1]));

  const id = `${GLOW_GROUP_ID_PREFIX}::${layerName}::GREY::${idx}`;

  const entity = viewer.entities.add({
    id,
    polygon: {
      hierarchy: new Cesium.PolygonHierarchy(positions),
      material: grayColor,
      outline: false,
    },
    properties: {
      isGrey: true,
      layerName,
    },
  });

  return entity;
};

export const renderGlowOutline = (
  viewer: Cesium.Viewer,
  feature: any,
  colorCss: string,
  glowPower: number,
  widthPx: number,
  layerName: string,
  idx: number,
) => {
  if (!viewer || !feature || !feature.geometry) return;

  const geomType = feature.geometry.type;

  const idBase = `${GLOW_GROUP_ID_PREFIX}::${layerName}::GLOW::${idx}`;

  const material = new Cesium.PolylineGlowMaterialProperty({
    color: Cesium.Color.fromCssColorString(colorCss),
    glowPower: Math.max(0, Math.min(1, glowPower)),
  });

  if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
    const coords = getOuterRingCoords(feature.geometry);
    if (!coords || coords.length < 2) return;

    const positions = coords.map((p: number[]) => Cesium.Cartesian3.fromDegrees(p[0], p[1]));

    return viewer.entities.add({
      id: idBase,
      polyline: {
        positions,
        width: Math.max(1, Math.round(widthPx)),
        material,
        clampToGround: false,
        arcType: Cesium.ArcType.GEODESIC,
      },
      properties: {
        isGlow: true,
        layerName,
      },
    });
  }

  if (geomType === 'LineString' || geomType === 'MultiLineString') {
    const lineSets = extractLinePositions(feature.geometry);

    lineSets.forEach((positions, lineIdx) => {
      viewer.entities.add({
        id: `${idBase}::LINE::${lineIdx}`,
        polyline: {
          positions,
          width: Math.max(1, Math.round(widthPx)),
          material,
          clampToGround: false,
          arcType: Cesium.ArcType.GEODESIC,
        },
        properties: {
          isGlow: true,
          layerName,
          isLine: true,
        },
      });
    });
  }
};

export const extractPolygonOutlinePositions = (geom: any) => {
  const rings: Cesium.Cartesian3[][] = [];

  if (geom.type === 'Polygon') {
    geom.coordinates.forEach((ring: number[][]) => {
      const positions = ring.map((c) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));
      if (positions.length > 0) rings.push(positions);
    });
  }

  if (geom.type === 'MultiPolygon') {
    geom.coordinates.forEach((polygon: number[][][]) => {
      polygon.forEach((ring: number[][]) => {
        const positions = ring.map((c) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));
        if (positions.length > 0) rings.push(positions);
      });
    });
  }

  return rings;
};

export const renderGreyscalePoint = (viewer: Cesium.Viewer, feature: any, grayColor: Cesium.Color, layerName: string, idx: number) => {
  if (!viewer || !feature || !feature.geometry) return;

  const coords = feature.geometry.coordinates;
  if (!coords || coords.length < 2) return;

  const position = Cesium.Cartesian3.fromDegrees(coords[0], coords[1]);

  const id = `${GLOW_GROUP_ID_PREFIX}::${layerName}::GREY_POINT::${idx}`;

  const entity = viewer.entities.add({
    id,
    position,
    point: {
      pixelSize: 12,
      color: grayColor,
      outlineColor: Cesium.Color.DARKGRAY,
      outlineWidth: 1,
    },
    properties: {
      isGrey: true,
      layerName,
      isPoint: true,
    },
  });

  return entity;
};

export const extractLinePositions = (geom: any): Cesium.Cartesian3[][] => {
  const lines: Cesium.Cartesian3[][] = [];

  if (!geom || !geom.type || !geom.coordinates) return lines;

  if (geom.type === 'LineString') {
    const positions = geom.coordinates
      .filter((c: number[]) => Array.isArray(c) && c.length >= 2)
      .map((c: number[]) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

    if (positions.length > 1) lines.push(positions);
  }

  if (geom.type === 'MultiLineString') {
    geom.coordinates.forEach((line: number[][]) => {
      const positions = line.filter((c) => Array.isArray(c) && c.length >= 2).map((c) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

      if (positions.length > 1) lines.push(positions);
    });
  }

  return lines;
};

export const renderGreyscaleLine = (viewer: Cesium.Viewer, feature: any, grayColor: Cesium.Color, layerName: string, idx: number) => {
  const geom = feature.geometry;
  if (!geom) return;

  const lines = extractLinePositions(geom);
  if (!lines.length) return;

  const idBase = `${GLOW_GROUP_ID_PREFIX}::${layerName}::GREY_LINE::${idx}`;

  lines.forEach((positions, lineIdx) => {
    viewer.entities.add({
      id: `${idBase}::${lineIdx}`,
      polyline: {
        positions,
        width: 2,
        material: grayColor,
        clampToGround: false,
      },
      properties: {
        isGrey: true,
        layerName,
        isLine: true,
      },
    });
  });
};
